"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

export const VALOR_AGREGADO_TABS = [
  {
    label: "Herramientas digitales" ,
    href: "/soluciones/valor-agregado-cat/herramientas-digitales",
    slug: "herramientas-digitales",
  },
  {
    label: "Servicio y soporte",
    href: "/soluciones/valor-agregado-cat/servicio-y-soporte",
    slug: "servicio-y-soporte",
    customLabel: "El respaldo que acompaña a tus equipos durante todo su ciclo de vida."
  },
  {
    label: "Soluciones financieras",
    href: "/soluciones/valor-agregado-cat/cuenta-comercial-cat",
    slug: "cuenta-comercial-cat",
  },
] as const;

export const VALOR_AGREGADO_TAB_SLUGS = VALOR_AGREGADO_TABS.map((tab) => tab.slug);

export function getActiveValorAgregadoTab(pathname: string) {
  return VALOR_AGREGADO_TABS.find((tab) => pathname === tab.href || pathname.startsWith(`${tab.href}/`));
}

export function isValorAgregadoTabRoot(pathname: string) {
  return VALOR_AGREGADO_TABS.some((tab) => tab.href === pathname);
}

function renderLabel(label: string) {
  const parts = label.split('®');
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && <sup className="text-[0.6em] align-super">®</sup>}
    </span>
  ));
}

export default function ValorAgregadoTabs() {
  const pathname = usePathname();
  const router = useRouter();
  const activeTab = getActiveValorAgregadoTab(pathname);

  const [fixed, setFixed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initialTop = useRef(0);

  useEffect(() => {
    if (!ref.current) return;

    initialTop.current = ref.current.offsetTop;

    const handleScroll = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;

      if (!isMobile) {
        setFixed(false);
        return;
      }

      setFixed(window.scrollY >= initialTop.current);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <nav aria-label="Secciones de Valor Agregado Cat" className="w-full md:pt-4">
      {/* Dropdown — solo móvil */}
      {fixed && <div className="h-[72px]" />}
      <div
        ref={ref}
        className={`md:hidden p-3 ${fixed
            ? "fixed top-16 left-0 right-0 z-20 bg-transparent"
            : "bg-white border-t-2 border-t-silver mb-4"
          }`}
      >
        <select
          className="w-full mt-2 mb-2 px-3 py-2.5 border border-gray-300 rounded-md text-sm bg-white text-dark02 font-medium focus:outline-none shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
          value={activeTab?.href || ""}
          onChange={(e) => router.push(e.target.value)}
        >
          {VALOR_AGREGADO_TABS.map((tab) => (
            <option key={tab.href} value={tab.href}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs grid — solo sm en adelante */}
      <ul className="hidden md:grid md:grid-cols-3 divide-x divide-gray01 bg-white">
        {VALOR_AGREGADO_TABS.map((tab) => {
          const isActive = activeTab?.href === tab.href;
          return (
            <li key={tab.href}>
              <Link
                href={tab.href}
                className={cn(
                  "flex items-center justify-center text-center px-4 py-4 sm:py-5 text-base sm:text-lg transition-colors",
                  isActive
                    ? "bg-white text-dark02 font-bold border-t-4 border-matco"
                    : "bg-gray01 text-gray03 font-medium border-t-4 border-transparent hover:bg-white hover:text-dark02"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                {renderLabel(tab.label)}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
